/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    /**
     * Swaps the "Complete your order" placeholder picks (from the drawer
     * settings collection) for Shopify's recommendations. The element's own
     * lazy loader watches the viewport, which never fires for content inside
     * the off-canvas drawer, so it is triggered here instead — once per first
     * cart item, as cart-items.js re-triggers it after quantity changes.
     */
    loadUpsell() {
      const upsell = this.querySelector('#cart-recommendations');
      if (!upsell || typeof upsell.init !== 'function') return;
      if (!upsell.dataset.productId) return;
      // The Selleasy campaign cards take over when there is an active in-cart
      // campaign (assets/cart-drawer-selleasy.js); otherwise the default flow.
      if (window.cibSelleasyUpsell && window.cibSelleasyUpsell.claim(upsell)) return;
      if (upsell.dataset.loadedFor === upsell.dataset.productId) return;

      upsell.dataset.loadedFor = upsell.dataset.productId;
      upsell.init();
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);

      // "Complete your order" carousel. Delegated on the drawer, as its markup
      // is replaced on every cart change; scroll doesn't bubble, so capture it.
      this.addEventListener('click', (evt) => {
        const btn = evt.target.closest('[data-upsell-nav]');
        if (btn) this.scrollUpsell(btn);
      });
      this.addEventListener('scroll', (evt) => {
        if (evt.target.matches && evt.target.matches('[data-upsell-track]')) {
          CartDrawer.updateUpsellNav(evt.target);
        }
      }, true);

      // With a mouse the row can also be dragged to scroll it (touch devices
      // swipe natively).
      if (window.matchMedia('(pointer: fine)').matches) this.bindUpsellDrag();
    }

    /**
     * Mouse drag-to-scroll for the "Complete your order" row. A drag doesn't
     * follow the link of the card it started on; clicks on the card's own
     * controls (variant, quantity, add to cart) are left alone.
     */
    bindUpsellDrag() {
      let drag = null;

      this.addEventListener('mousedown', (evt) => {
        const track = evt.target.closest('[data-upsell-track]');
        if (!track || evt.button !== 0) return;
        if (evt.target.closest('button, input, select, textarea, custom-select, [role="listbox"]')) return;
        drag = { track, startX: evt.clientX, startLeft: track.scrollLeft, moved: false };
      });

      window.addEventListener('mousemove', (evt) => {
        if (!drag) return;
        const dx = evt.clientX - drag.startX;
        if (!drag.moved && Math.abs(dx) < 5) return;
        if (!drag.moved) {
          drag.moved = true;
          drag.track.classList.add('is-dragging');
        }
        evt.preventDefault();
        drag.track.scrollLeft = drag.startLeft - dx;
      });

      window.addEventListener('mouseup', () => {
        if (!drag) return;
        const { track, moved } = drag;
        drag = null;
        if (!moved) return;
        track.classList.remove('is-dragging');
        // Settle on a card again (scroll-snap was off while dragging).
        const card = track.querySelector('.cart-drawer-upsell__item');
        const step = card ? card.offsetWidth + parseFloat(getComputedStyle(track).columnGap || 0) : 0;
        if (step) track.scrollTo({ left: Math.round(track.scrollLeft / step) * step, behavior: 'smooth' });
        // Swallow the click that ends the drag so it doesn't open a product.
        track.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, { capture: true, once: true });
      });
    }

    /**
     * Scrolls the "Complete your order" carousel by one view (two cards).
     * @param {Element} btn - The prev/next button.
     */
    scrollUpsell(btn) {
      const track = btn.closest('.cart-drawer-upsell__carousel')?.querySelector('[data-upsell-track]');
      if (!track) return;
      const dir = btn.dataset.upsellNav === 'prev' ? -1 : 1;
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }

    /**
     * Disables the prev/next button at either end of the carousel.
     * @param {Element} track - The scrolling card list.
     */
    static updateUpsellNav(track) {
      const carousel = track.closest('.cart-drawer-upsell__carousel');
      if (!carousel) return;
      const max = track.scrollWidth - track.clientWidth;
      const prev = carousel.querySelector('[data-upsell-nav="prev"]');
      const next = carousel.querySelector('[data-upsell-nav="next"]');
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';

        this.loadUpsell();
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      // Keep disclosures the customer opened (e.g. the Order Summary) open
      // across the re-render, like cart-items.js does for quantity changes.
      const openDisclosures = Array.from(this.querySelectorAll('details[data-persist-open][open]'))
        .map((el) => el.dataset.persistOpen);

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      openDisclosures.forEach((key) => {
        const el = this.querySelector(`details[data-persist-open="${key}"]`);
        if (el) el.open = true;
      });

      // The re-render brings a fresh "Complete your order" element (the cart
      // just changed); load its recommendations if the drawer is showing.
      if (this.hasAttribute('open')) this.loadUpsell();

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
